import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Moon, ArrowLeft, ArrowRight, MapPin, Clock, Calendar, Share2, Twitter, MessageCircle, Facebook, Copy, Check, FileText, Download, Crown, Lock, CreditCard, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { calculateNatal, calculateFullChart, fetchNakshatra, generateNatalReport, createCheckout, saveUserNatal, getReportUsage, purchaseAdditionalReport } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { trackEvent } from "@/lib/useAnalytics";
import { toast } from "sonner";
import { motion } from "framer-motion";
import BirthChartVisual from "@/components/BirthChartVisual";
import FullPlanetaryChart from "@/components/FullPlanetaryChart";

export default function Calculator() {
  const { user } = useAuth();
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthCity, setBirthCity] = useState("");
  const [result, setResult] = useState(null);
  const [fullChart, setFullChart] = useState(null);
  const [searchParams] = useSearchParams();
  const showReportMode = searchParams.get("report") === "true";
  const [nakshatraDetail, setNakshatraDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [usage, setUsage] = useState(null);
  const [showFullChart, setShowFullChart] = useState(false);
  const navigate = useNavigate();

  // Fetch usage on mount (for signed-in users)
  useEffect(() => {
    if (user) {
      getReportUsage().then(setUsage).catch(console.error);
    }
  }, [user]);
  
  const isPremium = usage?.is_premium;
  const canGenerateFree = usage?.can_generate_free;

  const handleCalculate = async (e) => {
    e.preventDefault();
    if (!birthDate || !birthTime || !birthCity) {
      toast.error("Please fill all fields");
      return;
    }
    setLoading(true);
    setResult(null);
    setFullChart(null);
    setNakshatraDetail(null);
    setShowFullChart(false);
    try {
      // Fetch basic natal data and full chart in parallel
      const [natalData, chartData] = await Promise.all([
        calculateNatal({
          birth_date: birthDate,
          birth_time: birthTime,
          birth_city: birthCity,
        }),
        calculateFullChart({
          birth_date: birthDate,
          birth_time: birthTime,
          birth_city: birthCity,
        }).catch(() => null), // Non-blocking - full chart is optional
      ]);
      
      setResult(natalData);
      setFullChart(chartData);
      trackEvent("nakshatra_calculator_run");
      localStorage.setItem("natal_result", JSON.stringify(natalData));
      
      // Save natal data to user profile if logged in
      if (user) {
        saveUserNatal({
          birth_date: birthDate,
          birth_time: birthTime,
          birth_city: birthCity,
          ...natalData,
        }).catch(() => {}); // Silent fail - non-critical
      }
      
      // Fetch full nakshatra details for birth traits
      const detail = await fetchNakshatra(natalData.nakshatra_slug).catch(() => null);
      setNakshatraDetail(detail);
    } catch (e) {
      const msg = e.response?.data?.detail || "Calculation failed. Check your inputs.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getShareUrl = () => {
    if (!result) return "";
    const base = window.location.origin;
    return `${base}/share/${result.nakshatra_slug}/${result.pada}`;
  };

  const getShareText = () => {
    if (!result) return "";
    let text = `My Natal Moon is in ${result.nakshatra_name}, Pada ${result.pada} (${result.degree_in_sign}\u00B0 ${result.sidereal_sign}).`;
    if (nakshatraDetail?.birth_traits) {
      const traits = nakshatraDetail.birth_traits.replace(/\*\*/g, "").slice(0, 200);
      text += `\n\n${traits}${nakshatraDetail.birth_traits.length > 200 ? "..." : ""}`;
    }
    text += `\n\nDiscover yours:`;
    return text;
  };

  const shareTwitter = () => {
    const text = encodeURIComponent(getShareText());
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`${getShareText()}\n${getShareUrl()}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const shareFacebook = () => {
    const url = encodeURIComponent(getShareUrl());
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank");
  };

  const copyToClipboard = async () => {
    const text = `${getShareText()}\n${getShareUrl()}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors" data-testid="calc-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Back</span>
          </Link>
        </div>
      </nav>

      <main className="pt-14 flex items-center justify-center min-h-[calc(100vh-56px)]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-lg px-6 py-12"
        >
          <div className="mb-10">
            <h1 className="text-4xl sm:text-5xl font-light text-white/95 mb-3" data-testid="calc-title">
              Natal Nakshatra
            </h1>
            <p className="text-white/40 text-sm">
              Discover your Moon's Nakshatra and Pada placement from your birth data.
            </p>
          </div>

          <form onSubmit={handleCalculate} className="space-y-8" data-testid="natal-calculator-form">
            <div className="space-y-2">
              <Label className="text-white/50 text-xs tracking-[0.15em] uppercase">Birth Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none pl-10 text-white/90 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-12"
                  data-testid="birth-date-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/50 text-xs tracking-[0.15em] uppercase">Birth Time</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  type="time"
                  value={birthTime}
                  onChange={(e) => setBirthTime(e.target.value)}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none pl-10 text-white/90 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-12"
                  data-testid="birth-time-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/50 text-xs tracking-[0.15em] uppercase">Birth City</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 z-10" />
                <CityAutocomplete
                  placeholder="e.g. New Delhi, Mumbai, London"
                  value={birthCity}
                  onChange={(val) => setBirthCity(val)}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none pl-10 text-white/90 placeholder:text-white/20 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-12"
                  testId="birth-city-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black hover:bg-white/90 rounded-full h-12 text-sm tracking-wider font-medium disabled:opacity-50"
              data-testid="calculate-btn"
            >
              {loading ? "Calculating..." : "Calculate Natal Nakshatra"}
            </Button>
          </form>

          {/* Result */}
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-10 space-y-6"
              data-testid="natal-result"
            >
              {/* Birth Chart Visual */}
              <BirthChartVisual result={result} />

              {/* Natal Card */}
              <div
                className="rounded-lg p-6 sm:p-8"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <p className="text-white/40 text-xs tracking-[0.2em] uppercase mb-4">Your Natal Moon</p>

                <h2 className="text-3xl sm:text-4xl font-light text-white/95 mb-2" data-testid="natal-nakshatra-name">
                  {result.nakshatra_name}
                </h2>
                <p className="text-white/60 text-lg mb-4" data-testid="natal-pada">
                  Pada {result.pada}
                </p>

                <div className="flex flex-wrap gap-x-6 gap-y-2 mb-6 text-sm">
                  <span className="text-white/40">
                    Tropical: <span className="text-white/70">{result.tropical_degree_in_sign?.toFixed(1) || result.degree_in_sign}&deg; {result.tropical_sign || result.sidereal_sign}</span>
                  </span>
                  <span className="text-white/40">
                    Nakshatra Ruler: <span className="text-white/70">{result.nakshatra_ruler || result.ruler}</span>
                  </span>
                </div>

                {/* Birth Traits */}
                {nakshatraDetail?.birth_traits && (
                  <div className="mt-6 pt-6 border-t border-white/5" data-testid="natal-birth-traits">
                    <p className="text-white/40 text-xs tracking-[0.15em] uppercase mb-3">Birth Traits</p>
                    <p className="text-white/60 text-sm leading-relaxed">
                      {nakshatraDetail.birth_traits.replace(/\*\*/g, "")}
                    </p>
                  </div>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Button
                    variant="ghost"
                    className="text-white/60 hover:text-white hover:bg-white/5 text-sm"
                    onClick={() => navigate(`/nakshatras/${result.nakshatra_slug}`)}
                    data-testid="explore-natal-btn"
                  >
                    Explore {result.nakshatra_name}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              {/* Full Planetary Chart Toggle */}
              {fullChart && (
                <div className="space-y-4">
                  <button
                    onClick={() => setShowFullChart(!showFullChart)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-left hover:bg-white/[0.02] transition-colors"
                    style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                    data-testid="full-chart-toggle"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                        <span className="text-amber-400 text-sm">☉</span>
                      </div>
                      <div>
                        <p className="text-white/80 text-sm font-medium">Full Planetary Chart</p>
                        <p className="text-white/40 text-xs">View all 9 planets with tropical signs & sidereal nakshatras</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-white/40 transition-transform ${showFullChart ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showFullChart && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FullPlanetaryChart 
                        planets={fullChart.planets} 
                        ascendant={fullChart.ascendant}
                        houses={fullChart.houses}
                      />
                    </motion.div>
                  )}
                </div>
              )}

              {/* Get Full Report CTA */}
              <div
                className="rounded-lg p-6 sm:p-8"
                style={{ background: isPremium ? "rgba(251,191,36,0.05)" : "rgba(255,255,255,0.04)", border: isPremium ? "1px solid rgba(251,191,36,0.2)" : "1px solid rgba(255,255,255,0.10)" }}
                data-testid="report-cta-section"
              >
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-white/50" />
                  <p className="text-white/50 text-xs tracking-[0.15em] uppercase">Detailed Natal Report</p>
                </div>
                <p className="text-white/70 text-sm mb-4">
                  Get a comprehensive AI-generated PDF report with your lunar identity, emotional patterns, relationship style, and growth path.
                </p>
                
                {isPremium ? (
                  <>
                    {/* Premium user indicator */}
                    <div className="flex items-center gap-2 mb-4">
                      <Crown className="w-3 h-3 text-amber-400" />
                      <span className="text-amber-400 text-xs">
                        Premium — {usage?.remaining_included || 0} included{(usage?.additional_credits || 0) > 0 ? ` + ${usage.additional_credits} purchased` : ""} remaining
                      </span>
                    </div>
                    {canGenerateFree ? (
                      <Button
                        onClick={async () => {
                          toast.info("Generating report...");
                          try {
                            const blob = await generateNatalReport({
                              birth_date: birthDate,
                              birth_time: birthTime,
                              birth_city: birthCity,
                              name: "Seeker",
                            });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `natal_report_${result.nakshatra_slug}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                            toast.success("Report downloaded!");
                            // Refresh usage
                            getReportUsage().then(setUsage).catch(console.error);
                          } catch (e) {
                            if (e.response?.status === 402) {
                              toast.error("Additional payment required");
                              getReportUsage().then(setUsage).catch(console.error);
                            } else {
                              toast.error("Report generation failed");
                            }
                          }
                        }}
                        className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 rounded-full px-6 h-10 text-sm gap-2"
                        data-testid="download-natal-report-btn"
                      >
                        <Download className="w-4 h-4" />
                        Download Full Report
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <Button
                          onClick={async () => {
                            trackEvent("checkout_started", { report: "additional_report", type: "natal_report" });
                            try {
                              const checkout = await purchaseAdditionalReport("natal_report");
                              if (checkout.url) window.location.href = checkout.url;
                            } catch {
                              toast.error("Failed to start checkout");
                            }
                          }}
                          className="w-full bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 rounded-full h-10 text-sm"
                          data-testid="purchase-additional-natal-btn"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Purchase Additional Report — $1.00
                        </Button>
                        <p className="text-white/30 text-xs text-center">Monthly allowance used. Resets {usage?.reset_date}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Non-premium user - show purchase options */}
                    <div className="flex items-center gap-2 mb-4">
                      <Lock className="w-3 h-3 text-white/40" />
                      <span className="text-white/40 text-xs">
                        Premium or single purchase required
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={async () => {
                          try {
                            trackEvent("checkout_started", { report: "natal_report" });
                            const checkout = await createCheckout({
                              package_id: "natal_report",
                              origin_url: window.location.origin,
                              metadata: { name: "Seeker", birth_date: birthDate, birth_time: birthTime, birth_city: birthCity },
                            });
                            if (checkout.url) window.location.href = checkout.url;
                          } catch { toast.error("Failed to initiate checkout"); }
                        }}
                        className="bg-white text-black hover:bg-white/90 rounded-full px-6 h-10 text-sm gap-2"
                        data-testid="buy-report-btn"
                      >
                        <FileText className="w-4 h-4" />
                        Get Full Report — $1.99
                      </Button>
                      <Link to="/pricing">
                        <Button
                          variant="outline"
                          className="border-amber-500/20 text-amber-400 hover:bg-amber-500/10 rounded-full px-6 h-10 text-sm gap-2"
                        >
                          <Crown className="w-4 h-4" />
                          Subscribe — $4.29/mo
                        </Button>
                      </Link>
                    </div>
                  </>
                )}
              </div>

              {/* Share Section */}
              <div
                className="rounded-lg p-6 sm:p-8"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                data-testid="share-section"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Share2 className="w-4 h-4 text-white/50" />
                  <p className="text-white/50 text-xs tracking-[0.15em] uppercase">Share Your Nakshatra</p>
                </div>

                <p className="text-white/40 text-sm mb-5">
                  Let others know your natal Moon placement and discover theirs.
                </p>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={shareTwitter}
                    className="bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 rounded-full px-5 h-10 text-sm gap-2"
                    data-testid="share-twitter-btn"
                  >
                    <Twitter className="w-4 h-4" />
                    Twitter / X
                  </Button>
                  <Button
                    onClick={shareWhatsApp}
                    className="bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 rounded-full px-5 h-10 text-sm gap-2"
                    data-testid="share-whatsapp-btn"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </Button>
                  <Button
                    onClick={shareFacebook}
                    className="bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 rounded-full px-5 h-10 text-sm gap-2"
                    data-testid="share-facebook-btn"
                  >
                    <Facebook className="w-4 h-4" />
                    Facebook
                  </Button>
                  <Button
                    onClick={copyToClipboard}
                    className="bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 rounded-full px-5 h-10 text-sm gap-2"
                    data-testid="share-copy-btn"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
